import React, { useEffect, useMemo, useState } from "react";
import { Box, Button, Stack, Typography, useTheme } from "@mui/material";
import { getAssetHierarchyPalette, getAxionFieldAlignSx, getContainedButtonSx, getOutlinedButtonSx } from "../../cmms/assetHierarchyStyles";
import { FormattedMessage, useIntl } from "react-intl";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import Popup from "../../../components/Popup";
import AxionControls from "../../../components/controls/AxionControls";
import { createSparePart, updateSparePart } from "../../../fetchers/cmmsSparePart";
import { getSparePartTypes } from "../../../fetchers/cmmsSparePartType";
import { getStorageLocations } from "../../../fetchers/cmmsStorageLocation";
import { pushErrorMessage, pushNotification } from "../../../utils/notifications";
import { formatCmmsError } from "../../cmms/components/CmmsStates";
import cmmsMessages from "../../cmms/cmmsMessages";
import messages from "../messages";
import { toNullableNumber, validateSparePartForm } from "../sparePartForm";
import FieldError from "./FieldError";

/*
 * Add or edit a spare parts register entry.
 *
 * The field grouping follows the cmms/poc dialog: identity first, then the stock parameters.
 * Its manufacturer, reorder quantity and lead time are still not here — those fields were
 * deliberately deferred when the register was designed (phase 10 §3.5), and a form that offers
 * them would be promising a register column that does not exist. The min and max levels are the
 * one part of that list the workshop came back and asked for, so they are here now (BE-15.12).
 *
 * IsActive is absent for a reason of its own: retiring a part is a decision with consequences
 * for its history, and it has an endpoint rather than a checkbox an edit could tick in passing.
 *
 * Every field here is free text bar two. The type and the storage location are dropdowns — the two
 * fields on this form whose values have to exist somewhere else first, and the reason the dialog
 * runs two queries of its own. They share a row because they are the same kind of question.
 *
 * What counts as valid is in ../sparePartForm.js, along with the rule that a blank box is not a
 * zero. It moved out so the level pair could be tested without rendering a dialog.
 */
const emptyForm = {
  partNumber: "",
  supplierArticleNumber: "",
  name: "",
  name2: "",
  description: "",
  unit: "",
  // "" is the empty option AxionSelect renders, not a type or a location whose id happens to be
  // blank. Turned back into null on submit.
  storageLocationId: "",
  sparePartTypeId: "",
  unitCost: "",
  reorderPoint: "",
  maxStockLevel: "",
};

export default function SparePartFormDialog({ open, onClose, sparePart, companyKey }) {
  const theme = useTheme();
  const cmmsColors = getAssetHierarchyPalette(theme.palette.mode);
  const intl = useIntl();
  const queryClient = useQueryClient();
  const isEdit = Boolean(sparePart?.id);

  const [form, setForm] = useState(emptyForm);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (!open) return;
    setForm(isEdit
      ? {
          partNumber: sparePart.partNumber ?? "",
          supplierArticleNumber: sparePart.supplierArticleNumber ?? "",
          name: sparePart.name ?? "",
          name2: sparePart.name2 ?? "",
          description: sparePart.description ?? "",
          unit: sparePart.unit ?? "",
          storageLocationId: sparePart.storageLocationId ?? "",
          sparePartTypeId: sparePart.sparePartTypeId ?? "",
          unitCost: sparePart.unitCost ?? "",
          reorderPoint: sparePart.reorderPoint ?? "",
          maxStockLevel: sparePart.maxStockLevel ?? "",
        }
      : emptyForm);
    setErrors({});
  }, [open, isEdit, sparePart]);

  const handleTextChange = (event) => {
    const { name, id, value } = event.target;
    setForm((current) => ({ ...current, [name || id]: value }));
  };

  /*
   * The shelves to choose from.
   *
   * Only while the dialog is open: the register page renders this component whether or not it is
   * showing, so an unconditional query would fetch the locations on every visit to a page that
   * may never open the form.
   *
   * Same query key the settings page writes through, so a shelf added there is in this list
   * without a reload. Active ones only — retiring a location is how you stop it being chosen.
   */
  const locationsQuery = useQuery(
    ["cmmsStorageLocations", companyKey, false],
    () => getStorageLocations(companyKey, false),
    { enabled: open && Boolean(companyKey), staleTime: 30000 },
  );

  // The kinds to choose from, on the same terms as the shelves above: only while the dialog is
  // open, the same key the settings tab writes through, and active ones only.
  const typesQuery = useQuery(
    ["cmmsSparePartTypes", companyKey, false],
    () => getSparePartTypes(companyKey, false),
    { enabled: open && Boolean(companyKey), staleTime: 30000 },
  );

  /*
   * The active locations, plus the one this part is already on if that one has been retired.
   *
   * Without the second half, opening a part that sits on a retired shelf would show an empty
   * location box, and saving anything else about the part — its name, its reorder point — would
   * move it off the shelf it is actually on. The server allows a retired location to stay put
   * precisely so that does not have to happen, and this is the half of that decision the form
   * has to keep. It is marked as inactive in the list so choosing it again is a decision rather
   * than an accident.
   *
   * The loaded check is what makes that label truthful. Before the query answers there is no
   * list to be absent from, so an ordinary active location would come up "(inactive)" for as
   * long as the request took — and the option still has to exist meanwhile, or MuiSelect holds a
   * value that matches no item and renders blank.
   */
  const locationOptions = useMemo(() => {
    const loaded = Array.isArray(locationsQuery.data);
    const active = (locationsQuery.data ?? []).map((location) => ({
      id: location.id,
      title: location.name,
    }));

    const currentId = sparePart?.storageLocationId;
    if (!currentId || active.some((option) => option.id === currentId)) return active;

    const currentName = sparePart.storageLocationName ?? String(currentId);
    return [
      ...active,
      {
        id: currentId,
        title: loaded
          ? intl.formatMessage(messages.fieldStorageLocationInactiveOption, { name: currentName })
          : currentName,
      },
    ];
  }, [locationsQuery.data, sparePart, intl]);

  /*
   * The same fold for the types, and for the same reason.
   *
   * SparePartService.RequireSparePartTypeAsync checks that the type exists and belongs to the
   * company, and deliberately does not check IsActive — so a part whose kind was retired last week
   * still saves. Without the appended option this form would be the thing that broke that: an
   * empty Type box, and every later edit silently stripping the type off the part.
   */
  const typeOptions = useMemo(() => {
    const loaded = Array.isArray(typesQuery.data);
    const active = (typesQuery.data ?? []).map((type) => ({
      id: type.id,
      title: type.name,
    }));

    const currentId = sparePart?.sparePartTypeId;
    if (!currentId || active.some((option) => option.id === currentId)) return active;

    const currentName = sparePart.sparePartTypeName ?? String(currentId);
    return [
      ...active,
      {
        id: currentId,
        title: loaded
          ? intl.formatMessage(messages.fieldSparePartTypeInactiveOption, { name: currentName })
          : currentName,
      },
    ];
  }, [typesQuery.data, sparePart, intl]);

  const saveMutation = useMutation({
    mutationFn: (payload) => (isEdit
      ? updateSparePart(sparePart.id, payload)
      : createSparePart(payload)),
  });

  const validate = () => {
    const next = validateSparePartForm(form, intl);
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;

    const payload = {
      companyKey,
      partNumber: form.partNumber.trim(),
      // Both must be sent even when blank. UpdateAsync assigns a fixed set of columns, so a key
      // missing from this literal is written as null — which is exactly how these two were being
      // cleared by any edit before this literal listed them.
      supplierArticleNumber: form.supplierArticleNumber?.trim() || null,
      name: form.name.trim(),
      name2: form.name2?.trim() || null,
      description: form.description?.trim() || null,
      unit: form.unit.trim(),
      // The empty option is "", which has to reach the server as null rather than as a location or
      // type id of nought. toNullableNumber already draws that line for the two numeric
      // fields below, so it draws it here too.
      storageLocationId: toNullableNumber(form.storageLocationId),
      sparePartTypeId: toNullableNumber(form.sparePartTypeId),
      unitCost: toNullableNumber(form.unitCost),
      reorderPoint: toNullableNumber(form.reorderPoint),
      maxStockLevel: toNullableNumber(form.maxStockLevel),
      // No created/updatedByUserId — the server takes it from the token.
    };

    try {
      await saveMutation.mutateAsync(payload);
      // Every filter variant of the register, because a new part belongs in whichever list is
      // on screen and an edited part number changes the sort.
      await queryClient.invalidateQueries({ queryKey: ["cmmsSpareParts"] });
      pushNotification(intl.formatMessage(isEdit ? messages.toastUpdated : messages.toastCreated));
      onClose?.();
    } catch (error) {
      // The server's own words: a duplicate part number comes back as
      // CMMS_SPARE_PART_DUPLICATE_NUMBER and says which number, which no generic text can.
      pushErrorMessage(formatCmmsError(error, intl.formatMessage(messages.saveFailed)));
    }
  };

  const submitting = saveMutation.isPending || saveMutation.isLoading;

  return (
    <Popup
      openPopup={open}
      setOpenPopup={(value) => { if (!value) onClose?.(); }}
      maxWidth="md"
      title={intl.formatMessage(isEdit ? messages.formEditTitle : messages.formAddTitle)}
    >
      <Box sx={{ display: "grid", gap: 2, p: 1 }}>
        {/*
          * The two numbers on one row, the two names on the next.
          *
          * With the ERP number gone the identity block is four fields, and pairing them by kind is
          * what makes the second name read as the continuation of the first: a Benämning 2 alone
          * across the full width looked like a different sort of field from the half-width
          * Benämning above it. The part number keeps the top left corner, so it is still the one
          * the eye lands on first.
          */}
        <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
          <Box sx={{ flex: 1 }}>
            <AxionControls.AxionTextBox
              name="partNumber"
              label={intl.formatMessage(messages.fieldPartNumber)}
              value={form.partNumber}
              onChange={handleTextChange}
            />
            <FieldError message={errors.partNumber} />
          </Box>
          <Box sx={{ flex: 1 }}>
            <AxionControls.AxionTextBox
              name="supplierArticleNumber"
              label={intl.formatMessage(messages.fieldSupplierArticleNumber)}
              value={form.supplierArticleNumber}
              onChange={handleTextChange}
            />
          </Box>
        </Stack>

        <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
          <Box sx={{ flex: 1 }}>
            <AxionControls.AxionTextBox
              name="name"
              label={intl.formatMessage(messages.fieldName)}
              value={form.name}
              onChange={handleTextChange}
            />
            <FieldError message={errors.name} />
          </Box>
          {/* Optional, so no FieldError — nothing about it can fail validation. */}
          <Box sx={{ flex: 1 }}>
            <AxionControls.AxionTextBox
              name="name2"
              label={intl.formatMessage(messages.fieldName2)}
              value={form.name2}
              onChange={handleTextChange}
            />
          </Box>
        </Stack>

        <AxionControls.AxionMultiLineText
          name="description"
          label={intl.formatMessage(messages.fieldDescription)}
          value={form.description}
          rows={2}
          onChange={handleTextChange}
        />

        {/*
          * The two lists, side by side.
          *
          * Neither is a box to type in. As free text, "Tätning", "tätning" and "Tätning " were
          * three kinds of seal — and "A-12", "A12" and "a-12 " three shelves — to every listing
          * that read them; the whole point of both tables is that they stop being possible to
          * create by accident. Empty stays a legitimate answer to each: a part with no kind
          * recorded, or nowhere, is one the register has always allowed.
          *
          * The search fields are on because a store can run to a hundred shelves and a workshop to
          * fifty kinds, and scrolling for one is worse than the text box this replaces.
          */}
        <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
          <Box sx={{ flex: 1 }}>
            <AxionControls.AxionSelect
              name="sparePartTypeId"
              label={intl.formatMessage(messages.fieldSparePartType)}
              // Lines it up with the rows of text boxes above and below. The helper carries the why.
              sx={getAxionFieldAlignSx()}
              value={form.sparePartTypeId}
              onChange={handleTextChange}
              options={typeOptions}
              noneText={intl.formatMessage(messages.fieldSparePartTypeNone)}
              searchField
              searchFieldPlaceholder={intl.formatMessage(messages.fieldSparePartTypeSearch)}
            />
            {typesQuery.error &&
              <Typography variant="caption" sx={{ color: "text.secondary" }}>
                <FormattedMessage {...messages.fieldSparePartTypeLoadFailed} />
              </Typography>
            }
            {/*
              * Only when the list is genuinely empty, which is a dead end otherwise: a dropdown
              * offering nothing but "no type" gives no hint that kinds are something you create,
              * or where. Not shown while loading or after a failure — data is undefined in both,
              * so ?.length === 0 is false, and neither of those means there are none.
              */}
            {!typesQuery.error && typesQuery.data?.length === 0 &&
              <Typography variant="caption" sx={{ color: "text.secondary" }}>
                <FormattedMessage {...messages.fieldSparePartTypeEmpty} />
              </Typography>
            }
          </Box>
          <Box sx={{ flex: 1 }}>
            <AxionControls.AxionSelect
              name="storageLocationId"
              label={intl.formatMessage(messages.fieldStorageLocation)}
              // Both selects need it, not just one: they already line up with each other, and the
              // helper is what lines the row up with the text boxes above and below.
              sx={getAxionFieldAlignSx()}
              value={form.storageLocationId}
              onChange={handleTextChange}
              options={locationOptions}
              noneText={intl.formatMessage(messages.fieldStorageLocationNone)}
              searchField
              searchFieldPlaceholder={intl.formatMessage(messages.fieldStorageLocationSearch)}
            />
            {locationsQuery.error &&
              <Typography variant="caption" sx={{ color: "text.secondary" }}>
                <FormattedMessage {...messages.fieldStorageLocationLoadFailed} />
              </Typography>
            }
            {/*
              * Only when the list is genuinely empty, which is a dead end otherwise: a dropdown
              * offering nothing but "no location" gives no hint that shelves are something you
              * create, or where. Not shown while loading or after a failure — neither of those
              * means there are none.
              */}
            {!locationsQuery.error && locationsQuery.data?.length === 0 &&
              <Typography variant="caption" sx={{ color: "text.secondary" }}>
                <FormattedMessage {...messages.fieldStorageLocationEmpty} />
              </Typography>
            }
          </Box>
        </Stack>

        {/*
          * The unit and the cost of one, then the two levels together.
          *
          * Min and max are a pair — order at this, order up to that — and splitting them across a
          * row boundary is what would make them read as two unrelated settings. So the pair takes
          * the row below, by the same reasoning as the two Benämning fields above.
          *
          * That left the price alone here beside a deliberately empty half, since stretched across
          * the full width it would have read as the most important field on the form, which a unit
          * cost is not. The unit fills that half now the type has taken its old place beside the
          * location: a unit and the cost of one unit are a pair on the same terms.
          */}
        <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
          <Box sx={{ flex: 1 }}>
            <AxionControls.AxionTextBox
              name="unit"
              label={intl.formatMessage(messages.fieldUnit)}
              value={form.unit}
              onChange={handleTextChange}
            />
            <FieldError message={errors.unit} />
          </Box>
          <Box sx={{ flex: 1 }}>
            <AxionControls.AxionTextBox
              name="unitCost"
              label={intl.formatMessage(messages.fieldUnitCost)}
              value={form.unitCost}
              onChange={handleTextChange}
            />
            <FieldError message={errors.unitCost} />
          </Box>
        </Stack>

        <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
          <Box sx={{ flex: 1 }}>
            <AxionControls.AxionTextBox
              name="reorderPoint"
              label={intl.formatMessage(messages.fieldReorderPoint)}
              value={form.reorderPoint}
              onChange={handleTextChange}
            />
            <FieldError message={errors.reorderPoint} />
            <Typography variant="caption" sx={{ color: "text.secondary" }}>
              <FormattedMessage {...messages.fieldReorderPointHint} />
            </Typography>
          </Box>
          {/*
            * Max carries the pair's one cross-field error: a max below the min. The server
            * refuses that too, but its sentence names columns rather than the labels on these
            * two boxes, and it arrives as a toast rather than under the field that is wrong.
            */}
          <Box sx={{ flex: 1 }}>
            <AxionControls.AxionTextBox
              name="maxStockLevel"
              label={intl.formatMessage(messages.fieldMaxStockLevel)}
              value={form.maxStockLevel}
              onChange={handleTextChange}
            />
            <FieldError message={errors.maxStockLevel} />
            <Typography variant="caption" sx={{ color: "text.secondary" }}>
              <FormattedMessage {...messages.fieldMaxStockLevelHint} />
            </Typography>
          </Box>
        </Stack>

        <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ mt: 1 }}>
          <Button variant="outlined" onClick={onClose} disabled={submitting} sx={getOutlinedButtonSx(cmmsColors)}>
            <FormattedMessage {...cmmsMessages.cancel} />
          </Button>
          <Button variant="contained" onClick={handleSubmit} disabled={submitting} sx={getContainedButtonSx(cmmsColors)}>
            <FormattedMessage {...(isEdit ? cmmsMessages.save : cmmsMessages.add)} />
          </Button>
        </Stack>
      </Box>
      <AxionControls.AxionBackdrop open={submitting} />
    </Popup>
  );
}
